import { FunctionComponent } from "react";

import { faArrowUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";

import { buildCampaignBuilderPath } from "../../../utils/campaignBuilderPath";
import { conversationCampaignLink, conversationStepLabel } from "../utils/conversationProvenance";

/**
 * Which campaign step produced a message, and a way into the campaign that defines it
 * (PREC-3908).
 *
 * The Outbox showed a lead's messages with no indication of where in the sequence each one came
 * from, so a thread was hard to reconstruct — "what actually happened to this lead". The step
 * number is the campaign builder's own node label, and the link opens that builder in a new tab
 * so the reader keeps their place in the thread.
 *
 * Renders nothing for a message that did not come from a campaign step (a manual send, an
 * inbox-scheduled reply) — there is no step to name and no campaign to open.
 */
export const ConversationProvenanceLine: FunctionComponent<{
	conversation: Conversation;
	campaignConfig: unknown;
	fallbackListId: number | null | undefined;
}> = ({ conversation, campaignConfig, fallbackListId }) => {
	const stepLabel = conversationStepLabel(conversation, campaignConfig);
	const campaign = conversationCampaignLink(conversation, campaignConfig, fallbackListId);

	if (!stepLabel && !campaign) {
		return null;
	}

	return (
		<div className="flex items-center gap-2 text-xs text-text-meta">
			{stepLabel && <span className="font-semibold">{stepLabel}</span>}
			{stepLabel && campaign && <span aria-hidden="true">·</span>}
			{campaign && (
				<a
					className="inline-flex cursor-pointer items-center gap-1 hover:text-brand-light"
					href={buildCampaignBuilderPath(campaign.listId, campaign)}
					rel="noopener noreferrer"
					target="_blank"
				>
					View the campaign
					<Icon className="h-3 w-3" icon={faArrowUpRightFromSquare} />
				</a>
			)}
		</div>
	);
};
